import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Newspaper,
    Pin,
    Edit3,
    Trash2,
    Send,
    Bell,
    BellOff,
    Loader2,
    Info,
    Megaphone,
    Quote,
    Flame,
    AlertTriangle,
    CheckCircle2,
    X,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import useDraft from "../../../hooks/useDraft";
import { useConfirm } from "../../ConfirmDialog";
import FileUpload from "../../FileUpload";
import AssoNewsImport from "./AssoNewsImport";

/**
 * Iter-27.68 — Pattern "Composer inline" pour les actualités générales (asso).
 * Même pattern que GratitudeMessagesTab : composer fixé en haut + liste en dessous.
 *
 * Les news postées remontent automatiquement dans /associations/:id (page formation).
 */
const NEWS_CATEGORIES = [
    { id: "info", label: "Info", color: "#7dd3fc", Icon: Info, hint: "Information générale" },
    { id: "campagne", label: "Campagne", color: "#a78bfa", Icon: Megaphone, hint: "Lancement de campagne" },
    { id: "temoignage", label: "Témoignage", color: "#fbbf24", Icon: Quote, hint: "Voix du terrain" },
    { id: "urgence", label: "Urgence", color: "#ef4444", Icon: Flame, hint: "Crise / situation critique" },
    { id: "alerte", label: "Alerte", color: "#fb923c", Icon: AlertTriangle, hint: "Information importante" },
];

function initialComposer() {
    return {
        _editingId: null,
        title: "",
        body: "",
        category: "info",
        image_url: "",
        pinned: false,
        notify_push: true,
    };
}

export default function AssoNewsTab({ onChanged }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [composer, setComposer] = useState(initialComposer());
    const [saving, setSaving] = useState(false);
    const composerRef = useRef(null);
    const confirm = useConfirm();

    // Iter-27.69 — Brouillon automatique localStorage
    const { clear: clearDraft } = useDraft(
        "shariti.draft.asso-news",
        composer,
        setComposer,
        {
            isInitial: (s) =>
                !s._editingId &&
                !s.title?.trim() &&
                !s.body?.trim() &&
                !s.image_url &&
                s.category === "info",
            onRestored: () =>
                toast.info("✏️ Brouillon récupéré", { duration: 3000 }),
        },
    );

    const reload = async () => {
        setLoading(true);
        try {
            const r = await api.get("/asso/news");
            setItems((r.data || []).filter((n) => n.category !== "gratitude"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    const startEdit = (item) => {
        setComposer({
            _editingId: item.news_id,
            title: item.title || "",
            body: item.body || "",
            category: item.category || "info",
            image_url: item.image_url || "",
            pinned: !!item.pinned,
            notify_push: false,
        });
        composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const cancelEdit = () => setComposer(initialComposer());

    const submit = async () => {
        if (!composer.title.trim() || !composer.body.trim()) {
            toast.error("Titre et message sont requis");
            return;
        }
        setSaving(true);
        try {
            if (composer._editingId) {
                await api.patch(`/asso/news/${composer._editingId}`, {
                    title: composer.title,
                    body: composer.body,
                    category: composer.category,
                    pinned: !!composer.pinned,
                    image_url: composer.image_url || null,
                });
                toast.success("Actualité mise à jour");
            } else {
                await api.post("/asso/news", {
                    title: composer.title,
                    body: composer.body,
                    category: composer.category,
                    pinned: !!composer.pinned,
                    notify_push: !!composer.notify_push,
                    image_url: composer.image_url || null,
                });
                toast.success(
                    composer.notify_push
                        ? "Actualité publiée + ambassadeurs notifiés"
                        : "Actualité publiée",
                );
            }
            setComposer(initialComposer());
            clearDraft();
            await reload();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (nid) => {
        const item = items.find((it) => it.news_id === nid);
        const ok = await confirm({
            title: "Supprimer cette actualité ?",
            description: item?.title
                ? `« ${item.title} » sera retirée du fil de tes ambassadeurs. Cette action est irréversible.`
                : "Cette actualité sera retirée du fil de tes ambassadeurs. Cette action est irréversible.",
            confirmLabel: "Supprimer",
            cancelLabel: "Annuler",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/asso/news/${nid}`);
            toast.success("Actualité supprimée");
            if (composer._editingId === nid) {
                setComposer(initialComposer());
                clearDraft();
            }
            await reload();
            onChanged?.();
        } catch {
            toast.error("Erreur");
        }
    };

    const isEditing = !!composer._editingId;
    const canSubmit = composer.title.trim() && composer.body.trim() && !saving;
    const activeCat = NEWS_CATEGORIES.find((c) => c.id === composer.category) || NEWS_CATEGORIES[0];

    return (
        <div className="space-y-6">
            {/* Iter-30.20 — Bouton "Importer un email" en haut, qui ouvre le module
                Lia version asso-side. Permet de coller un mail brut et de générer
                automatiquement plusieurs actus prêtes à publier. */}
            <ImportLauncher />

            {/* COMPOSER inline */}
            <motion.div
                ref={composerRef}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="asso-news-composer"
                className={`relative rounded-3xl border p-5 sm:p-6 transition-all ${
                    isEditing
                        ? "bg-shariti-orange/8 border-shariti-orange/40 ring-2 ring-shariti-orange/30"
                        : "bg-white/3 border-white/10"
                }`}
            >
                <div className="flex items-start gap-3 mb-4">
                    <div
                        className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
                        style={{
                            background: `${activeCat.color}1A`,
                            border: `1px solid ${activeCat.color}40`,
                        }}
                    >
                        <activeCat.Icon className="w-5 h-5" style={{ color: activeCat.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                            {isEditing ? "Modifier cette actualité" : "Publie une actualité"}
                        </p>
                        <p className="text-[11px] text-white/55 font-light mt-0.5">
                            {isEditing
                                ? "Modifie le contenu — il sera mis à jour dans la page formation des ambassadeurs."
                                : "Tes actualités apparaissent dans la page formation des ambassadeurs assignés à ta mission."}
                        </p>
                    </div>
                    {isEditing && (
                        <button
                            onClick={cancelEdit}
                            data-testid="asso-news-composer-cancel"
                            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-white/60 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/5 transition-all"
                        >
                            <X className="w-3.5 h-3.5" />
                            Annuler
                        </button>
                    )}
                </div>

                <div className="space-y-3">
                    {/* Sélecteur catégorie */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {NEWS_CATEGORIES.map((c) => {
                            const Icon = c.Icon;
                            const isSel = composer.category === c.id;
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => setComposer({ ...composer, category: c.id })}
                                    data-testid={`asso-news-cat-${c.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition-all border"
                                    style={
                                        isSel
                                            ? {
                                                  background: `${c.color}20`,
                                                  borderColor: `${c.color}80`,
                                                  color: c.color,
                                              }
                                            : {
                                                  background: "transparent",
                                                  borderColor: "rgba(255,255,255,0.1)",
                                                  color: "rgba(255,255,255,0.55)",
                                              }
                                    }
                                >
                                    <Icon className="w-3 h-3" />
                                    {c.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Titre */}
                    <input
                        type="text"
                        value={composer.title}
                        onChange={(e) =>
                            setComposer({ ...composer, title: e.target.value })
                        }
                        placeholder="Titre de l'actualité (ex : Lancement de notre campagne hiver)"
                        maxLength={200}
                        data-testid="asso-news-composer-title"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm sm:text-base font-bold outline-none focus:border-shariti-orange/40 placeholder:font-normal placeholder:text-white/35"
                    />

                    {/* Body */}
                    <textarea
                        rows={4}
                        value={composer.body}
                        onChange={(e) =>
                            setComposer({ ...composer, body: e.target.value })
                        }
                        placeholder="Détaille l'actualité, le contexte, l'appel à l'action…"
                        data-testid="asso-news-composer-body"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-shariti-orange/40 resize-y placeholder:text-white/35"
                    />

                    {/* Image / vidéo */}
                    {composer.image_url ? (
                        <div
                            data-testid="asso-news-composer-media-preview"
                            className="relative rounded-2xl overflow-hidden border border-shariti-orange/30 bg-black"
                        >
                            {/\.(mp4|webm|mov)(\?|$)/i.test(composer.image_url) ? (
                                <video
                                    src={composer.image_url}
                                    controls
                                    playsInline
                                    className="w-full max-h-72 object-contain bg-black"
                                />
                            ) : (
                                <img
                                    src={composer.image_url}
                                    alt=""
                                    className="w-full max-h-72 object-cover"
                                />
                            )}
                            <button
                                onClick={() =>
                                    setComposer({ ...composer, image_url: "" })
                                }
                                data-testid="asso-news-composer-media-remove"
                                className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-black/70 hover:bg-red-500/90 text-white text-[11px] font-bold backdrop-blur transition-all"
                            >
                                <Trash2 className="w-3 h-3" />
                                Retirer
                            </button>
                        </div>
                    ) : (
                        <NewsImageInput
                            title={composer.title}
                            body={composer.body}
                            category={composer.category}
                            onPicked={(url) =>
                                setComposer({ ...composer, image_url: url })
                            }
                        />
                    )}

                    {/* Toggles bar + Submit */}
                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/5">
                        <ToggleChip
                            active={composer.pinned}
                            onToggle={() =>
                                setComposer({ ...composer, pinned: !composer.pinned })
                            }
                            icon={Pin}
                            label="Épingler en haut"
                            color="#FCDB53"
                            testId="asso-news-composer-pinned"
                        />
                        {!isEditing && (
                            <ToggleChip
                                active={composer.notify_push}
                                onToggle={() =>
                                    setComposer({
                                        ...composer,
                                        notify_push: !composer.notify_push,
                                    })
                                }
                                icon={composer.notify_push ? Bell : BellOff}
                                label="Notifier les ambassadeurs"
                                color="#EE722E"
                                testId="asso-news-composer-notify"
                            />
                        )}
                        <button
                            onClick={submit}
                            disabled={!canSubmit}
                            data-testid="asso-news-composer-submit"
                            className="ml-auto inline-flex items-center gap-2 bg-shariti-orange hover:bg-shariti-orange/90 text-white font-extrabold px-5 py-2.5 rounded-2xl text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : isEditing ? (
                                <CheckCircle2 className="w-4 h-4" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                            {isEditing
                                ? "Mettre à jour"
                                : composer.notify_push
                                  ? "Publier + Notifier"
                                  : "Publier"}
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* LISTE des actualités */}
            <div>
                <p className="text-[11px] font-thin tracking-[0.2em] uppercase text-white/55 mb-3">
                    {items.length} actualité{items.length > 1 ? "s" : ""} publiée
                    {items.length > 1 ? "s" : ""}
                </p>

                {loading ? (
                    <p className="text-white/40 text-sm py-6 text-center">
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
                    </p>
                ) : items.length === 0 ? (
                    <div
                        className="rounded-3xl border border-dashed border-white/10 p-10 text-center"
                        data-testid="asso-news-empty"
                    >
                        <Newspaper className="w-10 h-10 mx-auto mb-2 text-white/30" />
                        <p className="text-sm text-white/65 font-bold">
                            Aucune actualité publiée.
                        </p>
                        <p className="text-xs text-white/45 mt-1">
                            Publie ta première actualité depuis la zone ci-dessus.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        <AnimatePresence>
                            {items.map((n) => {
                                const cat =
                                    NEWS_CATEGORIES.find((c) => c.id === n.category) ||
                                    NEWS_CATEGORIES[0];
                                const Icon = cat.Icon;
                                const isCurrentlyEditing = composer._editingId === n.news_id;
                                return (
                                    <motion.div
                                        key={n.news_id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, x: -10 }}
                                        layout
                                        data-testid={`asso-news-row-${n.news_id}`}
                                        className={`relative overflow-hidden rounded-2xl border transition-all ${
                                            isCurrentlyEditing
                                                ? "bg-shariti-orange/10 border-shariti-orange/40"
                                                : "bg-white/3 border-white/10 hover:border-white/20"
                                        }`}
                                    >
                                        {/* Iter-30.27 — Image hero (si présente) en haut de la card
                                            admin, cohérent avec la vue ambassadeur. */}
                                        {n.image_url && (
                                            <div className="relative w-full h-36 sm:h-44 bg-shariti-deep-black overflow-hidden">
                                                <img
                                                    src={n.image_url}
                                                    alt=""
                                                    loading="lazy"
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.currentTarget.parentElement.style.display = "none";
                                                    }}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-shariti-deep-black/85 via-transparent to-transparent pointer-events-none" />
                                                {/* Actions overlay top-right (visible aussi sans hover sur mobile) */}
                                                <div className="absolute top-2 right-2 flex items-center gap-1.5">
                                                    <button
                                                        onClick={() => startEdit(n)}
                                                        data-testid={`asso-news-edit-${n.news_id}`}
                                                        aria-label="Modifier"
                                                        className="p-2 rounded-lg bg-black/55 backdrop-blur-md hover:bg-shariti-orange/80 text-white transition-all"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => remove(n.news_id)}
                                                        data-testid={`asso-news-delete-${n.news_id}`}
                                                        aria-label="Supprimer"
                                                        className="p-2 rounded-lg bg-black/55 backdrop-blur-md hover:bg-red-500/85 text-white transition-all"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <div className={`flex items-start gap-3 ${n.image_url ? "p-3.5 sm:p-4" : "p-4"}`}>
                                            <div
                                                className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0"
                                                style={{
                                                    background: `${cat.color}1A`,
                                                    border: `1px solid ${cat.color}40`,
                                                }}
                                            >
                                                <Icon className="w-5 h-5" style={{ color: cat.color }} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span
                                                        className="text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                                        style={{
                                                            background: `${cat.color}20`,
                                                            color: cat.color,
                                                        }}
                                                    >
                                                        {cat.label}
                                                    </span>
                                                    {n.pinned && (
                                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase tracking-wider text-shariti-yellow bg-shariti-yellow/15 px-1.5 py-0.5 rounded-full">
                                                            <Pin className="w-2.5 h-2.5" /> Épinglé
                                                        </span>
                                                    )}
                                                    {isCurrentlyEditing && (
                                                        <span className="text-[9px] font-extrabold uppercase tracking-wider text-shariti-orange-light bg-shariti-orange/20 px-1.5 py-0.5 rounded-full animate-pulse">
                                                            Édition
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm sm:text-base font-extrabold text-white leading-snug mt-1 line-clamp-2">
                                                    {n.title}
                                                </p>
                                                <p className="text-xs text-white/60 line-clamp-2 leading-relaxed mt-0.5">
                                                    {n.body}
                                                </p>
                                            </div>
                                            {/* Actions inline (uniquement quand pas d'image, sinon en overlay) */}
                                            {!n.image_url && (
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <button
                                                        onClick={() => startEdit(n)}
                                                        data-testid={`asso-news-edit-${n.news_id}`}
                                                        aria-label="Modifier"
                                                        className="p-2 rounded-lg bg-white/5 hover:bg-shariti-orange/20 hover:text-shariti-orange-light text-white/65 transition-all"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => remove(n.news_id)}
                                                        data-testid={`asso-news-delete-${n.news_id}`}
                                                        aria-label="Supprimer"
                                                        className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/65 hover:text-red-300 transition-all"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </div>
    );
}


function ToggleChip({ active, onToggle, icon: Icon, label, color, testId }) {
    return (
        <button
            onClick={onToggle}
            data-testid={testId}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${
                active ? "text-shariti-black" : "text-white/55 border-white/10 hover:text-white"
            }`}
            style={active ? { background: color, borderColor: color } : {}}
        >
            <Icon className="w-3 h-3" />
            {label}
        </button>
    );
}

/**
 * Iter-30.20 — Bouton inline pour ouvrir le module d'import par email + Lia
 * (équivalent asso-side du "Import actu (Lia)" de l'admin).
 */
function ImportLauncher() {
    const [open, setOpen] = useState(false);
    return (
        <div>
            {!open ? (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="asso-news-import-launch"
                    className="w-full inline-flex items-center justify-between gap-3 rounded-3xl bg-shariti-orange/10 hover:bg-shariti-orange/15 border border-shariti-orange/30 px-5 py-4 transition-all text-left group"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-shariti-orange/20 flex items-center justify-center border border-shariti-orange/40">
                            <Sparkles className="w-5 h-5 text-shariti-orange-light" />
                        </div>
                        <div>
                            <p className="text-sm font-extrabold text-white">
                                Importer plusieurs actus depuis un email
                            </p>
                            <p className="text-[11px] text-white/55 leading-tight mt-0.5">
                                Colle ta newsletter, on extrait et structure chaque actu prête à publier.
                            </p>
                        </div>
                    </div>
                    <span className="text-shariti-orange-light text-[10px] font-extrabold tracking-[0.22em] uppercase group-hover:translate-x-1 transition-transform">
                        Ouvrir →
                    </span>
                </button>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center justify-end">
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="asso-news-import-close"
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-white/55 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/5 transition-all"
                        >
                            <X className="w-3.5 h-3.5" />
                            Fermer l&apos;import
                        </button>
                    </div>
                    <AssoNewsImport />
                </div>
            )}
        </div>
    );
}


/**
 * Iter-30.30 — Choix de l'image cover de l'actu : upload classique OU
 * génération IA via Gemini Nano Banana.
 *
 * UX : on présente les 2 options côte à côte. Le bouton "Générer avec IA" est
 * désactivé tant que le titre est vide (sinon l'image générée serait
 * générique). Pendant la génération (10-30s) on affiche un spinner.
 */
function NewsImageInput({ title, body, category, onPicked }) {
    const [generating, setGenerating] = useState(false);

    const titleReady = (title || "").trim().length >= 5;

    const generate = async () => {
        if (!titleReady) {
            toast.error("Renseigne un titre (5 caractères min.) pour générer une image cohérente");
            return;
        }
        setGenerating(true);
        const toastId = toast.loading("Génération en cours… (≈ 15-30 s)");
        try {
            const r = await api.post("/asso/news/generate-image", {
                title,
                body: body || "",
                category: category || "info",
            });
            const url = r.data?.image_url;
            if (!url) {
                throw new Error("Pas d'URL retournée");
            }
            onPicked(url);
            toast.success("Image générée !", { id: toastId });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur génération IA", { id: toastId });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="space-y-2.5" data-testid="asso-news-image-input">
            {/* Bouton IA — proéminent, hover orange */}
            <button
                type="button"
                onClick={generate}
                disabled={generating || !titleReady}
                data-testid="asso-news-generate-image-btn"
                className={`w-full inline-flex items-center justify-between gap-3 rounded-2xl px-4 py-3 transition-all text-left border ${
                    generating
                        ? "bg-shariti-orange/10 border-shariti-orange/30 cursor-wait"
                        : titleReady
                          ? "bg-shariti-orange/8 border-shariti-orange/30 hover:bg-shariti-orange/15 hover:border-shariti-orange/50"
                          : "bg-white/3 border-white/10 opacity-60 cursor-not-allowed"
                }`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-shariti-orange/20 border border-shariti-orange/40 flex items-center justify-center shrink-0">
                        {generating ? (
                            <Loader2 className="w-4 h-4 text-shariti-orange-light animate-spin" />
                        ) : (
                            <Sparkles className="w-4 h-4 text-shariti-orange-light" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <p className="text-[13px] font-extrabold text-white leading-tight">
                            {generating ? "Génération en cours…" : "Générer une image avec l'IA"}
                        </p>
                        <p className="text-[10.5px] text-white/55 leading-tight mt-0.5 line-clamp-1">
                            {titleReady
                                ? "Image cover photo-réaliste à partir du titre + corps"
                                : "Renseigne d'abord le titre de l'actu"}
                        </p>
                    </div>
                </div>
                {!generating && (
                    <span className="text-[9px] font-extrabold tracking-[0.22em] uppercase text-shariti-orange-light shrink-0">
                        Nano Banana
                    </span>
                )}
            </button>

            {/* Séparateur visuel "ou" */}
            <div className="flex items-center gap-2 my-1">
                <div className="flex-1 h-px bg-white/5" />
                <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-white/35">
                    ou
                </span>
                <div className="flex-1 h-px bg-white/5" />
            </div>

            {/* Upload classique */}
            <FileUpload
                kind="image"
                currentUrl=""
                onUploaded={onPicked}
                testId="asso-news-composer-media-upload"
            />
        </div>
    );
}
